/**
 * DashboardDetailView — inline-opened dashboard view (Phase 5 extract from
 * pages/Dashboards.jsx). Pure render component; all state + handlers flow
 * via the `ctx` prop bag, mirroring the Phase 4 ConnectorWizard split.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, AlertCircle, Palette, FileDown, Loader2 } from "lucide-react";
import { exportDashboardToPdf } from "@/lib/export-pdf";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import InlineEditableTitle from "@/components/dashboards/InlineEditableTitle";
import DashboardGrid from "@/components/dashboards/DashboardGrid";
import DashboardSourcesDrawer from "@/components/dashboards/DashboardSourcesDrawer";
import AddSourceDialog from "@/components/dashboards/AddSourceDialog";
import ShareDashboardDialog from "@/components/dashboards/ShareDashboardDialog";
import DashboardHeaderActions from "@/components/dashboards/DashboardHeaderActions";
import SchemaMatchErrorDialog from "@/components/dashboards/SchemaMatchErrorDialog";
import LockedKpiStrip from "@/components/dashboards/LockedKpiStrip";
import LockedKpiStripBoundary from "@/components/dashboards/LockedKpiStripBoundary";
import { DashboardScheduleControl } from "@/components/dashboards/DashboardScheduleControl";
import PushdownConfigDialog from "@/components/dashboards/PushdownConfigDialog";
import GalleryPickerDialog, { galleryItemToWidget } from "@/components/dashboards/GalleryPickerDialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { applyDashboardTheme } from "@/lib/settings-context";


/**
 * iter-121.132 — SA-only "Apply Dashboard Theme" pill. Re-fetches the
 * tenant's effective Dashboard Theme from settings and re-stamps the
 * --chart-N / --dash-shadow / data-skin CSS vars on :root. Useful when
 * the SA tweaked the theme in another tab and wants the change visible
 * here without a full page reload.
 */
function ApplyThemePill() {
    const [busy, setBusy] = React.useState(false);
    const onApply = async () => {
        setBusy(true);
        try {
            const r = await api.get("/settings/dashboard-theme");
            const cfgToApply = r?.data?.effective_config || r?.data?.config;
            if (cfgToApply && r?.data?.registry) {
                applyDashboardTheme(cfgToApply, r.data.registry);
                toast.success("Dashboard theme applied");
            } else {
                toast.info("No theme configured yet — open Settings → Dashboard Theme");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to apply theme");
        } finally {
            setBusy(false);
        }
    };
    return (
        <Button
            variant="outline" size="sm"
            className="rounded-sm h-8 text-xs"
            onClick={onApply}
            disabled={busy}
            data-testid="prebuilt-apply-theme-btn"
        >
            <Palette className="h-3 w-3 mr-1.5" />
            {busy ? "Applying…" : "Apply Theme"}
        </Button>
    );
}

export default function DashboardDetailView({ ctx }) {
    const {
        opened, setOpened, setDashboards, nav,
        canWriteData,canWriteDashboard, isSuperAdmin,
        wiringHref, clone,
        sourcesOpen, setSourcesOpen,
        addSourceOpen, setAddSourceOpen,
        shareOpen, setShareOpen,
        galleryOpen, setGalleryOpen,
        pushdownOpen, setPushdownOpen,
        schemaMismatch, setSchemaMismatch,
        load,
    } = ctx;
    const src = opened.source || {};
    const isAuto = !!src.auto;
    const canEdit = !opened.is_template && (opened.can_edit !== undefined ? opened.can_edit : canWriteDashboard);

    // PDF export — captures the full dashboard content (title, KPI cards,
    // charts, tables, filters, theme) and paginates it across A4 pages.
    // Interactive chrome is stripped in the export copy via [data-pdf-exclude].
    const exportRootRef = React.useRef(null);
    const [exporting, setExporting] = React.useState(false);
    const handleExportPdf = async () => {
        if (exporting) return;
        setExporting(true);
        const t = toast.loading(`Preparing "${opened.name}" for export…`);
        try {
            await exportDashboardToPdf(exportRootRef.current, opened.name);
            toast.success(`Exported ${opened.name} as PDF`, { id: t });
        } catch (e) {
            toast.error(e?.message || "Export failed", { id: t });
        } finally {
            setExporting(false);
        }
    };

    const renameDashboard = async (newName) => {
        if (!newName || newName === opened.name) return;
        try {
            const r = await api.patch(`/dashboards/${opened.id}`, { name: newName });
            setOpened(r.data);
            setDashboards((prev) => prev.map((d) => d.id === r.data.id ? r.data : d));
            toast.success("Dashboard renamed");
        } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
    };

    // iter-119x (#3): the empty-state banner in LockedKpiStrip used to <Link>
    // to /data — losing dashboard context. It now dispatches
    // `dashboard:open-add-source`; we open the same AddSourceDialog the
    // tile-level "Connect data" button uses.
    React.useEffect(() => {
        const onOpen = (e) => {
            if (e?.detail?.id && opened?.id && e.detail.id !== opened.id) return;
            setAddSourceOpen(true);
        };
        window.addEventListener("dashboard:open-add-source", onOpen);
        return () => window.removeEventListener("dashboard:open-add-source", onOpen);
    }, [opened?.id, setAddSourceOpen]);

    // iter-119x (#4): when ANY user finishes a connector ingest, reload the
    // dashboard so newly-ingested rows reflect immediately (was previously
    // requiring a hard refresh for non-SA roles).
    React.useEffect(() => {
        const onIngested = () => {
            load();
            window.dispatchEvent(new CustomEvent("dashboard:refresh", { detail: { id: opened?.id } }));
        };
        window.addEventListener("data:ingested", onIngested);
        return () => window.removeEventListener("data:ingested", onIngested);
    }, [opened?.id, load]);

    // Deep-link support: `?highlight_widget=<id>` scrolls to the widget
    // card and flashes a ring outline for 2.5s. Used by the dataset
    // Lineage graph (Widget Formula Insight) to jump straight from a
    // widget name into the widget itself.
    React.useEffect(() => {
        if (!opened?.id) return;
        const params = new URLSearchParams(window.location.search);
        const wid = params.get("highlight_widget");
        if (!wid) return;
        // Retry a few times because widgets render async after data-load.
        let cancelled = false;
        let tries = 0;
        const tick = () => {
            if (cancelled) return;
            const el = document.querySelector(`[data-testid="widget-${wid}"]`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.classList.add("ring-2", "ring-primary", "ring-offset-2", "transition-shadow");
                setTimeout(() => {
                    el.classList.remove("ring-2", "ring-primary", "ring-offset-2");
                }, 2500);
                // Clear the param so a subsequent refresh doesn't re-fire.
                params.delete("highlight_widget");
                const q = params.toString();
                const nextUrl = window.location.pathname + (q ? `?${q}` : "");
                window.history.replaceState({}, "", nextUrl);
                return;
            }
            tries += 1;
            if (tries < 20) setTimeout(tick, 250);
        };
        // Delay first attempt so grid mounts.
        setTimeout(tick, 400);
        return () => { cancelled = true; };
    }, [opened?.id]);

    return (
        <div className="fadein" ref={exportRootRef}>
            <PageHeader
                eyebrow={opened.is_template ? "Template preview" : isAuto ? "Auto-generated Dashboard" : "Dashboard"}
                title={canEdit ? <InlineEditableTitle value={opened.name} onSave={renameDashboard} testId="dashboard-title-edit" /> : opened.name}
                description={opened.description ? <span className="line-clamp-2" title={opened.description}>{opened.description}</span> : null}
                testId="dashboard-view-header"
                actions={
                    <div className="flex items-center gap-2 flex-wrap justify-end" data-pdf-exclude="true">
                        <DashboardHeaderActions
                        dashboard={opened}
                        canEdit={canEdit}
                        canWriteData={canWriteData}
                        onBack={() => { setOpened(null); nav("/dashboards"); }}
                        // iter-121.132 — Apply Theme pill now sits beside
                        // the back arrow (was previously rendered ahead
                        // of the entire DashboardHeaderActions cluster,
                        // which pushed it left of the back affordance).
                        // Scoped to instantiated dashboards that carry
                        // a `source_gallery_template_id` or are
                        // explicitly unified. Prebuilt TEMPLATE previews
                        // ride a different route (GalleryPreview); the
                        // pill there lives directly on that page.
                        afterBackSlot={isSuperAdmin && (
                            opened.source_gallery_template_id
                            || opened.is_unified
                        ) ? <ApplyThemePill /> : null}
                        onViewSourceDataset={() => nav(`/data?tab=datasets&dataset=${src.dataset_id}`)}
                        onRebuildAuto={async () => {
                            try {
                                const r = await api.post(`/dashboards/${opened.id}/rebuild-auto`);
                                setOpened(r.data);
                                setDashboards((prev) => prev.map((d) => d.id === r.data.id ? r.data : d));
                                toast.success("Auto widgets regenerated", { description: `${(r.data.widgets || []).length} widgets now on the dashboard.` });
                            } catch (e) { toast.error(e?.response?.data?.detail || "Rebuild failed"); }
                        }}
                        onViewSources={() => setSourcesOpen(true)}
                        onRegenerateFromTemplate={async () => {
                            try {
                                const r = await api.post(`/dashboards/${opened.id}/regenerate-from-template`);
                                setOpened(r.data);
                                setDashboards((prev) => prev.map((d) => d.id === r.data.id ? r.data : d));
                                toast.success("Dashboard regenerated from template", { description: `${(r.data.widgets || []).length} widgets refreshed; favourites & layout preserved.` });
                            } catch (e) { toast.error(e?.response?.data?.detail || "Regenerate failed"); }
                        }}
                        onOpenGallery={() => setGalleryOpen(true)}
                        onAddSource={() => setAddSourceOpen(true)}
                        onShare={() => setShareOpen(true)}
                        onClone={() => clone(opened)}
                        scheduleControl={
                            <DashboardScheduleControl
                                dashboard={opened}
                                onChanged={(secs) => setOpened((p) => p ? { ...p, auto_refresh_seconds: secs } : p)}
                            />
                        }
                    />
                    <Button variant="outline" size="sm" className="rounded-sm h-9" onClick={handleExportPdf} disabled={exporting}
                        title="Export this dashboard as PDF" data-pdf-exclude="true" data-testid="dashboard-export-pdf-btn">
                        {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}
                        {exporting ? "Exporting…" : "Export"}
                    </Button>
                    </div>
                }
            />
            <PageContent>
                {isAuto && (
                    <div className="mb-4 flex items-center gap-2 text-xs text-muted-foreground font-mono" data-testid="dashboard-auto-meta">
                        {(opened.tags || []).includes("personal") && (
                            <Badge variant="outline">PERSONAL</Badge>
                        )}
                        {src.filename && <span>source: {src.filename}</span>}
                        {src.last_refreshed_at && <span>· refreshed {new Date(src.last_refreshed_at).toLocaleString()}</span>}
                        <RefreshCw className="h-3 w-3 opacity-40" />
                    </div>
                )}
                {/* iter-2026 — scope strip shrunk to its content width; the
                    freed space on the right now hosts the PDF Export action.
                    Export is always available for an opened dashboard; the
                    button itself is excluded from the exported PDF. */}
                {(opened.missing_models || []).length > 0 && (
                    <div className="mb-3 inline-flex items-start gap-2 px-3 py-2 rounded-sm border border-amber-500/40 bg-amber-500/5 text-xs"
                        data-testid="dashboard-needs-data-source">
                        <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
                        <div>
                            <div className="font-medium text-foreground">Needs a data source</div>
                            <div className="text-[11px] mt-0.5 text-muted-foreground">
                                The data model this dashboard uses was deleted ({opened.missing_models.join(", ")}). Its charts and formulas are kept; connect it to a model to show data again.
                            </div>
                        </div>
                    </div>
                )}
                {opened.data_scope_warning && (<div className="mb-3 flex items-start justify-between gap-3">
                    {opened.data_scope_warning ? (
                        <div
                            className={`inline-flex items-start gap-2 px-3 py-2 rounded-sm border text-xs max-w-full md:max-w-[72%] ${
                                opened.data_scope_warning.level === "warning"
                                    ? "border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-300"
                                    : "border-border bg-muted/30 text-muted-foreground"
                            }`}
                            data-testid="dashboard-scope-warning">
                            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                                <div className="font-medium text-foreground">{opened.data_scope_warning.message}</div>
                                <div className="text-[11px] mt-0.5 opacity-80 font-mono">
                                    Scope: {opened.data_scope_warning.scope}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>)}
                {opened.source_gallery_template_id && (
                    <LockedKpiStripBoundary>
                        <LockedKpiStrip dashboard={opened}
                                        onDashboardUpdated={(d) => { setOpened(d); setDashboards((prev) => prev.map((x) => x.id === d.id ? d : x)); }} />
                    </LockedKpiStripBoundary>
                )}
                <DashboardGrid dashboard={opened} onDashboardUpdated={(d) => { setOpened(d); setDashboards((prev) => prev.map((x) => x.id === d.id ? d : x)); }} />
                {isSuperAdmin && opened.source_gallery_template_id && (
                    <div className="mt-4 flex items-center justify-end gap-2" data-pdf-exclude="true">
                        <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs"
                                onClick={() => setPushdownOpen(true)}
                                data-testid="prebuilt-pushdown-config-btn">
                            Configure SQL pushdown
                            {opened.pushdown?.connector_id && (
                                <span className="ml-2 px-1.5 py-0.5 rounded-sm text-[9px] font-mono bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                                    ACTIVE
                                </span>
                            )}
                        </Button>
                    </div>
                )}
                {isSuperAdmin && opened.source_gallery_template_id && (
                    <PushdownConfigDialog open={pushdownOpen} onOpenChange={setPushdownOpen}
                        dashboard={opened}
                        onSaved={(pd) => setOpened((prev) => ({ ...prev, pushdown: pd }))} />
                )}
            </PageContent>
            <GalleryPickerDialog
                open={galleryOpen}
                onClose={() => setGalleryOpen(false)}
                onAdd={async (item) => {
                    const widget = galleryItemToWidget(item);
                    if (!widget) {
                        toast.error("This gallery item cannot be added as a widget");
                        return;
                    }
                    const next = [...(opened.widgets || []), widget];
                    // iter-119ai — gallery items decomposed from prebuilt templates
                    // carry `data_model_bindings` at the ITEM ROOT (set by
                    // `_decompose_widgets_to_gallery`). The dashboard needs those
                    // bindings at its OWN root to drive `joined-query`. On a Blank
                    // Dashboard there are no bindings yet — without this merge the
                    // widget renders with no data. We union by `model_id` so re-adds
                    // from the same model don't duplicate.
                    const itemBindings = Array.isArray(item.data_model_bindings) ? item.data_model_bindings : [];
                    const existingBindings = Array.isArray(opened.data_model_bindings) ? opened.data_model_bindings : [];
                    const seen = new Set(existingBindings.map((b) => (typeof b === "string" ? b : b?.model_id)).filter(Boolean));
                    const mergedBindings = [...existingBindings];
                    for (const b of itemBindings) {
                        const key = typeof b === "string" ? b : b?.model_id;
                        if (key && !seen.has(key)) { mergedBindings.push(b); seen.add(key); }
                    }
                    const patch = { widgets: next };
                    if (mergedBindings.length > existingBindings.length) {
                        patch.data_model_bindings = mergedBindings;
                    }
                    try {
                        const r = await api.patch(`/dashboards/${opened.id}`, patch);
                        setOpened(r.data);
                        setDashboards((prev) => prev.map((d) => d.id === r.data.id ? r.data : d));
                        toast.success(`Added "${item.name}"`);
                        setGalleryOpen(false);
                    } catch (e) {
                        toast.error(e?.response?.data?.detail || "Failed to add widget");
                    }
                }}
            />
            <DashboardSourcesDrawer dashboardId={opened.id} open={sourcesOpen} onClose={() => setSourcesOpen(false)} prebuilt={!!opened.source_gallery_template_id} />
            <AddSourceDialog
                open={addSourceOpen}
                onClose={() => setAddSourceOpen(false)}
                dashboardId={opened.id}
                dashboardName={opened.name}
                onAttached={() => { setAddSourceOpen(false); load(); }}
                onConnectNew={() => nav(wiringHref({ dashboard_id: opened.id, append: 1 }, `/dashboards/${opened.id}`))}
                onSchemaMismatch={(payload) => setSchemaMismatch(payload)}
            />
            <ShareDashboardDialog
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                dashboardId={opened.id}
                dashboardName={opened.name}
            />
            <SchemaMatchErrorDialog
                open={!!schemaMismatch}
                onClose={() => setSchemaMismatch(null)}
                payload={schemaMismatch}
            />
        </div>
    );
}
